import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { dataURLToBlob } from 'blob-util'
import * as markerjs2 from 'markerjs2'

import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import ZoomOut from '@mui/icons-material/ZoomOut'
import Slider from '@mui/material/Slider'
import ZoomIn from '@mui/icons-material/ZoomIn'

export type PhotoViewerProps = {
  disableAnnotation?: boolean
  extension: string
  name: string
  onError?: (error?: any) => void
  onSave?: (file: File) => void
  src: string
  fallBackSrc: string
}

export default function PhotoViewer(props: PhotoViewerProps) {
  const { disableAnnotation, extension, name, onError, onSave, src, fallBackSrc } = props
  const { t } = useTranslation()

  const markerAreaWrapperRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)

  const [error, setError] = useState<boolean>(false)
  const [imgSrc, setImgSrc] = useState<string>()
  const [imageHeight, setImageHeight] = useState(0)
  const [zoom, setZoom] = useState<number>(50)
  const [zoomStyle, setZoomStyle] = useState<React.CSSProperties>({})

  const handleError = useCallback(() => {
    setImgSrc(fallBackSrc)
    setError(true)
    onError?.()
  }, [fallBackSrc, onError])

  useEffect(() => {
    if (src) {
      fetch(src)
        .then((response) => response.blob())
        .then((blob: Blob) => {
          setImgSrc(URL.createObjectURL(blob))
        })
        .catch(handleError)
    }
  }, [handleError, src])

  const handleLoad = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const img = event.currentTarget
    const naturalHeight = img.naturalHeight
    const naturalWidth = img.naturalWidth

    // Get available viewport dimensions (accounting for the zoom controls at bottom)
    const viewportHeight = window.innerHeight - 150 // Reserve space for zoom controls
    const viewportWidth = window.innerWidth - 100 // Some padding

    // Calculate scale factors to fit the image within viewport
    const heightScale = viewportHeight / naturalHeight
    const widthScale = viewportWidth / naturalWidth

    // Use the smaller scale to ensure image fits in both dimensions
    const scale = Math.min(heightScale, widthScale, 1) // Don't zoom in beyond natural size

    // Calculate the initial zoom level based on the scale
    // The zoom formula: if zoom < 50, height = imageHeight - (50 - zoom) * 10
    //                   if zoom >= 50, height = imageHeight + (zoom - 50) * 10
    // We want: naturalHeight * scale = result height

    const targetHeight = naturalHeight * scale
    let initialZoom = 50

    if (targetHeight < naturalHeight) {
      // Need to zoom out (zoom < 50)
      // targetHeight = naturalHeight - (50 - zoom) * 10
      // (50 - zoom) * 10 = naturalHeight - targetHeight
      // zoom = 50 - (naturalHeight - targetHeight) / 10
      initialZoom = Math.max(10, 50 - (naturalHeight - targetHeight) / 10)
    } else if (targetHeight > naturalHeight) {
      // Need to zoom in (zoom > 50)
      // targetHeight = naturalHeight + (zoom - 50) * 10
      // zoom = 50 + (targetHeight - naturalHeight) / 10
      initialZoom = Math.min(150, 50 + (targetHeight - naturalHeight) / 10)
    }

    setImageHeight(naturalHeight)
    setZoom(initialZoom)
    setZoomStyle({
      height: targetHeight,
      width: 'auto',
    })
  }

  const handleZoomChange = (event: Event, newValue: number | number[]) => {
    if (Array.isArray(newValue)) return
    else {
      setZoom(newValue)
      if (imgRef && imgRef.current) {
        if (newValue < 50) {
          setZoomStyle({
            height: imageHeight - (50 - newValue) * 10,
            width: 'auto',
          })
        } else {
          setZoomStyle({
            height: imageHeight + (newValue - 50) * 10,
            width: 'auto',
          })
        }
      }
    }
  }

  const showMarkerArea = () => {
    if (!!disableAnnotation || !markerAreaWrapperRef.current || !imgRef.current) return

    // create a marker.js MarkerArea
    const markerArea = new markerjs2.MarkerArea(imgRef.current)

    markerArea.renderAtNaturalSize = true
    markerArea.settings.displayMode = 'popup'
    markerArea.targetRoot = markerAreaWrapperRef.current

    // attach an event handler to assign annotated image back to our image element
    markerArea.addEventListener('render', (event) => {
      const annotatedFileName = name.includes('-annotated-')
        ? `${name.replace(/-annotated-.*$/, '')}-annotated-${Date.now()}.${extension}`
        : `${name}-annotated-${Date.now()}.${extension}`
      onSave?.(new File([dataURLToBlob(event.dataUrl)], annotatedFileName, { type: `image/${extension}` }))
    })

    // launch marker.js
    markerArea.show()
  }

  const valueLabelFormat = (value: number) => {
    return `${value}%`
  }

  const zoomIn = () => {
    const currentZoom = zoom
    setZoom(currentZoom + 10)
    setZoomStyle({
      height: imageHeight + (currentZoom - 50) * 10,
      width: 'auto',
    })
  }

  const zoomOut = () => {
    const currentZoom = zoom
    setZoom(currentZoom - 10)
    setZoomStyle({
      height: imageHeight - (50 - currentZoom) * 10,
      width: 'auto',
    })
  }

  return (
    <Stack alignItems="center" spacing={2} sx={{ width: '100%', position: 'relative' }}>
      <Box ref={markerAreaWrapperRef} sx={{ position: 'fixed', top: 0, left: 0, zIndex: (theme) => theme.zIndex.appBar + 1 }} />
      {imgSrc && <img onLoad={handleLoad} onClick={showMarkerArea} style={zoomStyle} ref={imgRef} src={imgSrc} alt="viewer" onError={handleError} />}
      {error && (
        <Typography variant="subtitle1" component="div">
          {t('component.ui.filePreview.photo.notAvailable')}
        </Typography>
      )}
      <Stack spacing={2} direction="row" sx={{ mb: 1, width: '50%', position: 'fixed', bottom: '30px' }} alignItems="center">
        <ZoomOut style={{ cursor: 'pointer' }} onClick={zoomOut} />
        <Slider
          valueLabelDisplay="on"
          valueLabelFormat={valueLabelFormat}
          max={150}
          min={10}
          step={10}
          value={zoom}
          onChange={handleZoomChange}
        />
        <ZoomIn style={{ cursor: 'pointer' }} onClick={zoomIn} />
      </Stack>
    </Stack>
  )
}
